import { ReactNode, useMemo, useState } from 'react'
import { UserIcon } from '@heroicons/react/24/solid'
import { Field, Label } from './base/fieldset'
import { Listbox, ListboxLabel, ListboxOption } from './base/listbox'
import clsx from 'clsx'

interface Tab {
  name: string
  icon: typeof UserIcon
  component: ReactNode
}

interface Props {
  tabs: Tab[]
}

const tabCols: { [count: number]: string } = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4',
  5: 'grid-cols-5',
}

export const Tabs = ({ tabs }: Props) => {
  const [currentTab, setCurrentTab] = useState(0)

  const groupedTabs = useMemo(() => {
    const groups = []
    let currentGroup = []

    for (let i = 0; i < tabs.length; i++) {
      if (i % 5 == 0 && i > 0) {
        groups.push(currentGroup)
        currentGroup = []
      }

      currentGroup.push(tabs[i])
    }

    if (currentGroup.length > 0) groups.push(currentGroup)

    return groups
  }, [tabs])

  return (
    <div>
      <div className="sm:hidden">
        <Field>
          <Label>Select a tab</Label>
          <Listbox defaultValue={0} onChange={setCurrentTab}>
            { tabs.map((tab, index) => (
              <ListboxOption key={tab.name} value={index}>
                <tab.icon />
                <ListboxLabel>{ tab.name }</ListboxLabel>
              </ListboxOption>
            )) }
          </Listbox>
        </Field>
      </div>
      <div className="hidden sm:block">
        <div className="border-b border-zinc-950/10 dark:border-zinc-50/10">
          <nav aria-label="Tabs" className="-mb-px grid grid-cols-1 gap-y-2">
            {
              groupedTabs.map((tabGroup, layerIndex) => (
                <div key={layerIndex} className={`grid ${tabGroup.length < 5 ? tabCols[tabGroup.length] : tabCols[5]} justify-items-center w-full`}>
                  { tabGroup.map((tab, index) => (
                    <span key={tab.name}>
                      <a
                        aria-current={currentTab == (layerIndex * 5) + index ? 'page' : undefined}
                        className={clsx(
                          currentTab == (layerIndex * 5) + index
                            ? 'border-zinc-900 dark:border-white text-zinc-900 dark:text-white'
                            : `border-transparent text-zinc-500 hover:border-zinc-800 hover:text-zinc-900
                              dark:hover:border-white dark:hover:text-white`,
                          'group inline-flex items-center border-b-2 px-1 pb-4 text-sm font-medium',
                        )}
                        onClick={() => setCurrentTab((layerIndex * 5) + index)}
                      >
                        <tab.icon
                          aria-hidden="true"
                          className={clsx(
                            currentTab == (layerIndex * 5) + index
                              ? 'text-zinc-900 dark:text-white'
                              : 'text-zinc-400 group-hover:text-zinc-900 dark:group-hover:text-white',
                            '-ml-0.5 mr-2 h-5 w-5',
                          )}
                        />
                        <span>{tab.name}</span>
                      </a>
                    </span>
                  )) }
                </div>
              ))
            }
          </nav>
        </div>
        <div className="pt-4">
          { tabs[currentTab].component }
        </div>
      </div>
    </div>
  )
}
